import {
  render,
  MARK_BOLD,
  MARK_LINK,
  MARK_STRIKE,
  MARK_UNDERLINE,
  NODE_UL,
  NODE_OL,
  NODE_LI,
  NODE_HEADING,
  NODE_PARAGRAPH,
  NODE_QUOTE,
  MARK_CODE,
  MARK_HIGHLIGHT,
} from "storyblok-rich-text-react-renderer";
import { StoryblokComponent } from "@storyblok/react";
import { ReactNode } from "react";
import { Size } from "apps/website/components/layout/Spacer/Spacer.map";
import Text from "apps/website/components/base/Text/Text";
import List from "apps/website/components/base/List/List";
import ListItem from "apps/website/components/base/ListItem/ListItem";
import { Display } from "apps/website/components/base/Text/Text.map";
import {
  Interpolator,
  interpolateString,
} from "libs/form-utils/src/lib/interpolate";
import StoryModal from "apps/website/components/feature/Modal/StoryModal/StoryModal";
import { IStoryBlokRelation } from "apps/website/components/page/api.types";
import {
  LinkWithQuery,
} from "apps/website/components/overrides/LinkWithQuery/LinkWithQuery";

import { redirectToCorrectInternalPath } from "./links";

interface IRichTextOptionLinks {
  display?: Display;
  transformer?: (string: string) => string;
}

interface IRichTextInterpolationOptions {
  values: Record<string, string>;
}

interface IRichTextTagOptions {
  MARK_CODE?: {
    display?: "default" | "subtitle"
  }
}
export interface IRichTextOptions {
  interpolation?: IRichTextInterpolationOptions;
  unwrapResolvers?: boolean;
  links?: IRichTextOptionLinks;
  tags?: IRichTextTagOptions;
  // "article" opts in to the blog styling: the code mark doubles as a text
  // highlight and blockquotes render as pull-quotes (TECH-2710). Keeping it
  // behind a variant means other rich text (incl. existing code-mark usage) is
  // unaffected.
  variant?: "article";
}

interface IRichTextInlineStoryModal extends Record<string, unknown> {
  _uid: string;
  button_text: string;
  story: IStoryBlokRelation<ReactNode>;
}

type RichTextInlineStoryModal = Record<keyof IRichTextInlineStoryModal, unknown>;

// Chartreuse text highlight (brand yellow). Rendered as <mark> so it inherits
// the surrounding font/size/weight; box-decoration-clone gives each wrapped
// line its own highlight box, matching the Figma design. The colour is locked
// to the brand — any colour set on the Storyblok highlight mark is ignored.
const renderHighlight = (children: ReactNode) => (
  <mark className="box-decoration-clone px-1 py-0.5 text-black bg-brand">
    { children }
  </mark>
);

export const renderStoryBlokRichText = (richText: string, options?: IRichTextOptions) => {
  // Interpolation only applies to leaf text (strings). Non-string children
  // (nested React nodes) are passed through untouched.
  const interpolate = (children: ReactNode) => (options?.interpolation && typeof children === "string"
    ? interpolateString(children, options.interpolation.values, Interpolator.FRONTEND)
    : children);

  const renderLinkText = (children: ReactNode) => (
    <Text tag="span" display={options?.links?.display ?? "subtitle"} className="underline" size="inherit">{ interpolate(children) }</Text>
  );

  const transformHref = (href: string) => (options?.links?.transformer ? options.links.transformer(href) : href);

  // Encapsulates the shared null / unwrapResolvers branching for node resolvers.
  const wrapNode = (children: ReactNode, renderNode: () => JSX.Element): JSX.Element | null => {
    if (children === null) {
      return null;
    }
    if (options?.unwrapResolvers) {
      return <>{ children }</>;
    }
    return renderNode();
  };

  return render(richText, {
    markResolvers: {
      [MARK_CODE]: (children) => {
        if (children === null) {
          return null;
        }
        if (options?.unwrapResolvers) {
          return <>{ children }</>;
        }
        // In articles the code mark doubles as a text highlight (TECH-2710).
        if (options?.variant === "article") {
          return renderHighlight(interpolate(children));
        }
        return <Text tag="span" size="inherit" align="inherit" display={options?.tags?.MARK_CODE?.display || "subtitle"}>{ interpolate(children) }</Text>;
      },
      [MARK_HIGHLIGHT]: (children) => {
        if (children === null) {
          return null;
        }
        if (options?.unwrapResolvers) {
          return <>{ children }</>;
        }
        return renderHighlight(interpolate(children));
      },
      [MARK_BOLD]: (children) => (children !== null ? <Text tag="strong" display="subtitle" size="inherit">{ interpolate(children) }</Text> : null),
      [MARK_STRIKE]: (children) => (children !== null ? <Text tag="s" className="line-through" size="inherit">{ interpolate(children) }</Text> : null),
      [MARK_UNDERLINE]: (children) => (children !== null ? <Text tag="u" className="underline" size="inherit">{ interpolate(children) }</Text> : null),
      [MARK_LINK]: (children, props) => {
        const { linktype, href } = props;
        if (linktype === "email") {
          // Email link text is intentionally not interpolated (preserves prior behaviour).
          return <a href={`mailto:${href ? transformHref(href) : href}`}><Text tag="span" display={options?.links?.display ?? "subtitle"} className="underline" size="inherit">{ children }</Text></a>;
        }
        if (href?.match(/^(https?:)?\/\//)) {
          return <a href={href ? transformHref(href) : href} target="_blank" rel="nofollow noopener noreferrer">{ renderLinkText(children) }</a>;
        }
        return <LinkWithQuery href={transformHref(redirectToCorrectInternalPath(href ?? ""))}>{ renderLinkText(children) }</LinkWithQuery>;
      },
    },
    nodeResolvers: {
      [NODE_UL]: (children) => wrapNode(children, () => <List listStyle="disc" className="mb-4 ml-8">{ interpolate(children) }</List>),
      [NODE_OL]: (children) => wrapNode(children, () => <List tag="ol" listStyle="decimal" className="mb-4 ml-8">{ interpolate(children) }</List>),
      [NODE_LI]: (children) => wrapNode(children, () => <ListItem display="list-item">{ interpolate(children) }</ListItem>),
      [NODE_HEADING]: (children, { level }) => wrapNode(children, () => <Text tag={`h${level}`} display="subtitle" size={level === 3 ? "md" : "default"} className="mb-4">{ interpolate(children) }</Text>),
      [NODE_PARAGRAPH]: (children) => wrapNode(children, () => <Text size="inherit" align="inherit">{ children }</Text>),
      // Pull-quote styling for articles only (TECH-2710). The inner paragraph
      // Text sets its own font, so we override it to the display font here.
      ...(options?.variant === "article" ? {
        [NODE_QUOTE]: (children: ReactNode) => wrapNode(children, () => (
          <blockquote className="my-6 text-black tracking-[-0.4px] leading-[1.2] text-base lg:text-lg [&_p]:!font-display">
            { interpolate(children) }
          </blockquote>
        )),
      } : {}),
    },
    blokResolvers: {
      rich_text_inline_story_modal: ( // eslint-disable-line
        props: RichTextInlineStoryModal,
      ) => (
        <StoryModal id={props._uid as string} buttonText={props.button_text as string} buttonPadding={(props?.padding || "none") as Size}>
          <StoryblokComponent blok={(props.story as IStoryBlokRelation<ReactNode>).content} />
        </StoryModal>
      ),
    },
  });
};
